/**
 * PDP metafield product switcher → full-document fetch + marker-targeted section swap + Alpine/GSAP rebound.
 */
(function () {
  if (window.__pdpSectionProductSwitcherInit) {
    return;
  }
  window.__pdpSectionProductSwitcherInit = true;

  var SECTION_MARKER = '[data-pdp-personalized-section]';

  /** @returns {HTMLElement|null} */
  function closestShopifySection(el) {
    return el && el.closest ? el.closest('.shopify-section') : null;
  }

  /**
   * Section wrapper that contains the personalized PDP marker (handles nested/rare layouts).
   * @returns {HTMLElement|null}
   */
  function closestMarkedShopifySection(el) {
    var sec = closestShopifySection(el);
    while (sec) {
      if (sec.querySelector(SECTION_MARKER)) return sec;
      var parent = sec.parentElement;
      sec = parent ? parent.closest('.shopify-section') : null;
    }
    return null;
  }

  /** @param {HTMLElement} sectionWrap */
  function killScrollTriggersInSection(sectionWrap) {
    if (typeof ScrollTrigger === 'undefined') {
      return;
    }
    try {
      ScrollTrigger.getAll().forEach(function (st) {
        try {
          var tr = st.trigger;
          if (tr && tr.nodeType === 1 && sectionWrap.contains(tr)) {
            st.kill(false);
          }
        } catch (e) {}
      });
    } catch (e) {}
  }

  function activateInlineScripts(scope) {
    scope.querySelectorAll('script:not([src])').forEach(function (old) {
      var type = (old.getAttribute('type') || '').trim();
      if (
        type.indexOf('json') !== -1 ||
        type.indexOf('ld+json') !== -1 ||
        type === 'application/ld+json'
      ) {
        return;
      }
      var code = old.textContent || '';
      if (!code.trim()) return;
      var neo = document.createElement('script');
      if (type) neo.type = old.type;
      neo.textContent = code;
      document.body.appendChild(neo);
      neo.remove();
    });
  }

  /**
   * Remove hoisted styles tied to an old section id; append matching {% style %} output for the new id from SRA HTML.
   *
   * @param {string} oldSectionDomId element id e.g. shopify-section-template--…__main
   * @param {string} newSectionDomId
   * @param {Document} parsedDoc
   */
  function syncHoistedStylesAfterSwap(oldSectionDomId, newSectionDomId, parsedDoc) {
    if (!document.head || !parsedDoc) return;

    function removeForSection(domId) {
      if (!domId) return;
      var needle = '#' + domId;
      document.head.querySelectorAll('style').forEach(function (s) {
        if (s.textContent.indexOf(needle) !== -1) {
          s.remove();
        }
      });
    }

    removeForSection(oldSectionDomId);

    if (!newSectionDomId) return;
    var sectionNeedle = '#' + newSectionDomId;
    var dedupe = Object.create(null);
    var toAppend = [];

    function collectHoisted(scope) {
      if (!scope || !scope.querySelectorAll) return;
      scope.querySelectorAll('style[data-shopify]').forEach(function (s) {
        if (s.textContent.indexOf(sectionNeedle) === -1) return;
        var text = s.textContent;
        if (dedupe[text]) return;
        dedupe[text] = true;
        toAppend.push(s);
      });
    }

    if (parsedDoc.head) collectHoisted(parsedDoc.head);
    var freshWrap = parsedDoc.getElementById(newSectionDomId);
    if (freshWrap) collectHoisted(freshWrap);

    toAppend.forEach(function (s) {
      var neo = document.createElement('style');
      for (var i = 0; i < s.attributes.length; i++) {
        var attr = s.attributes[i];
        neo.setAttribute(attr.name, attr.value);
      }
      neo.textContent = s.textContent;
      document.head.appendChild(neo);
    });
  }

  function clearPersonalizedLoadingState() {
    var marker = document.querySelector(SECTION_MARKER);
    var wrap = marker && marker.closest('.shopify-section');
    if (wrap) {
      wrap.classList.remove('opacity-70', 'pointer-events-none');
    }
  }

  document.addEventListener(
    'change',
    async function (e) {
      var target = e.target;
      if (
        !target ||
        target.type !== 'radio' ||
        !target.hasAttribute('data-pdp-section-switch')
      ) {
        return;
      }

      var handle = target.getAttribute('data-product-handle');
      if (!handle) return;

      var pathParts = window.location.pathname.split('/').filter(Boolean);
      /** simple check: locale-prefixed /es/products/handle vs /products/handle */
      var idx = pathParts.lastIndexOf('products');
      var curHandle =
        idx !== -1 && pathParts[idx + 1]
          ? pathParts[idx + 1].split('?')[0]
          : '';
      if (
        decodeURIComponent(curHandle).toLowerCase() ===
        decodeURIComponent(handle).toLowerCase()
      ) {
        return;
      }

      var sectionWrap = closestMarkedShopifySection(target);
      if (!sectionWrap) return;

      var rootHref = '/';
      try {
        rootHref =
          typeof window.Shopify !== 'undefined' &&
          Shopify.routes &&
          Shopify.routes.root
            ? Shopify.routes.root
            : '/';
      } catch (err) {
        rootHref = '/';
      }
      if (!rootHref.endsWith('/')) rootHref += '/';

      sectionWrap.classList.add('opacity-70', 'pointer-events-none');

      killScrollTriggersInSection(sectionWrap);

      var basePath = rootHref.replace(/\/+$/, '');
      var fetchUrl = new URL(window.location.href);
      fetchUrl.pathname = basePath + '/products/' + encodeURIComponent(handle);
      fetchUrl.searchParams.delete('section_id');
      fetchUrl.searchParams.delete('variant');
      fetchUrl.hash = '';

      var oldSectionDomId = sectionWrap.id;

      try {
        var res = await fetch(fetchUrl.toString(), {
          credentials: 'same-origin',
        });
        var html = await res.text();
        var doc = new DOMParser().parseFromString(html, 'text/html');

        var freshMarker = doc.querySelector(SECTION_MARKER);
        var freshSection = freshMarker ? freshMarker.closest('.shopify-section') : null;
        if (!freshSection || !freshSection.id) {
          throw new Error(
            '[pdp-switcher] Personalized section marker missing in full-document response',
          );
        }

        // mutateDom pauses Alpine's observer so it doesn't init the new section on insert
        // (before its inline scripts run); initTree below inits it exactly once.
        if (window.Alpine && typeof Alpine.mutateDom === 'function') {
          Alpine.mutateDom(function () {
            sectionWrap.outerHTML = freshSection.outerHTML;
          });
        } else {
          sectionWrap.outerHTML = freshSection.outerHTML;
        }

        var liveMarker = document.querySelector(SECTION_MARKER);
        var newSection = liveMarker ? liveMarker.closest('.shopify-section') : null;
        if (!newSection || !newSection.id) {
          throw new Error('[pdp-switcher] Personalized section missing after DOM swap');
        }

        syncHoistedStylesAfterSwap(oldSectionDomId, newSection.id, doc);

        activateInlineScripts(newSection);

        try {
          if (window.Alpine && typeof Alpine.initTree === 'function') {
            Alpine.initTree(newSection);
          }
        } catch (alpineErr) {
          console.error('[pdp-switcher] Alpine.initTree', alpineErr);
        }

        if (typeof window.__reinitPdpRedesignStickyPin === 'function') {
          window.__reinitPdpRedesignStickyPin();
        }

        window.dispatchEvent(
          new CustomEvent('gallery-layout-changed', {
            bubbles: true,
            detail: { reason: 'pdp-section-switch' },
          }),
        );
        window.dispatchEvent(
          new CustomEvent('pdp-product-switched', {
            bubbles: true,
            detail: { handle: handle },
          }),
        );

        var cleanBrowse = new URL(fetchUrl.href);
        cleanBrowse.searchParams.delete('section_id');
        cleanBrowse.searchParams.delete('variant');
        window.history.replaceState(
          {},
          '',
          cleanBrowse.pathname + cleanBrowse.search,
        );
      } catch (err) {
        console.error('[pdp-switcher]', err);
      } finally {
        clearPersonalizedLoadingState();
      }
    },
    false,
  );
})();
